/**
 * Disponibilités mentor : normalisation des plages horaires.
 *
 * Historiquement, un jour était stocké comme un unique couple ["09:00","17:00"].
 * Désormais on autorise plusieurs plages dans la même journée :
 *   [["09:00","12:00"], ["14:00","18:00"]]
 *
 * Pour rester rétrocompatible (les mentors déjà enregistrés ont l'ancien
 * format), on détecte la forme à la lecture : si le premier élément est une
 * chaîne, c'est l'ancien format (1 plage) ; si c'est un tableau, c'est le
 * nouveau (N plages). Le stockage reste une colonne JSON côté Symfony, donc
 * aucune migration n'est nécessaire.
 *
 * normalizeRanges renvoie TOUJOURS un tableau de couples [[start, end], ...]
 * (éventuellement vide), trié par heure de début et débarrassé des entrées
 * invalides.
 */
export function normalizeRanges(day) {
    if (!Array.isArray(day) || day.length === 0) return [];

    // Ancien format : ["09:00", "17:00"]
    if (typeof day[0] === "string") {
        const [start, end] = day;
        if (!start || !end) return [];
        return [[start, end]];
    }

    // Nouveau format : [["09:00","12:00"], ["14:00","18:00"]]
    return day
        .filter(
            (range) =>
                Array.isArray(range) &&
                typeof range[0] === "string" &&
                typeof range[1] === "string" &&
                range[0] &&
                range[1],
        )
        .map((range) => [range[0], range[1]])
        .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0));
}

/** "HH:mm" -> minutes depuis minuit. */
export function timeToMinutes(time) {
    const [h, m] = String(time).split(":");
    return parseInt(h, 10) * 60 + parseInt(m, 10);
}

/** minutes depuis minuit -> "HH:mm" (borné à 23:59). */
export function minutesToTime(total) {
    const capped = Math.max(0, Math.min(total, 23 * 60 + 59));
    const h = Math.floor(capped / 60);
    const m = capped % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/**
 * Valide une liste de plages pour un jour donné.
 * Renvoie { ok: true } ou { ok: false, error: "range" | "overlap" } :
 *   - "range"   : une plage dont la fin n'est pas après le début
 *   - "overlap" : deux plages qui se chevauchent
 */
export function validateDayRanges(ranges) {
    const sorted = [...ranges].sort((a, b) =>
        a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0,
    );
    for (let i = 0; i < sorted.length; i++) {
        const [start, end] = sorted[i];
        if (timeToMinutes(start) >= timeToMinutes(end)) {
            return { ok: false, error: "range" };
        }
        if (i > 0 && timeToMinutes(start) < timeToMinutes(sorted[i - 1][1])) {
            return { ok: false, error: "overlap" };
        }
    }
    return { ok: true };
}
